import '@vaadin/date-picker';
// eslint-disable-next-line no-duplicate-imports
import type {
    DatePickerChangeEvent,
    DatePickerValueChangedEvent,
    DatePicker as VaadinDatePicker,
} from '@vaadin/date-picker';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmInputFieldBase } from './tm-input-field.base';

export const tagName = 'tm-date-picker';
@customElement(tagName)
export class TmDatePicker extends TmInputFieldBase<Date> {
    @property({ attribute: false, reflect: false })
    public min?: Date;

    @property({ attribute: false, reflect: false })
    public max?: Date;

    @property({ type: Boolean })
    public disableAutoOpen = true;

    @property({ attribute: false })
    public isDateDisabled?: object;

    private readonly datePickerRef = createRef<VaadinDatePicker>();

    private set datePicker(picker: VaadinDatePicker) {
        if (picker) {
            picker.i18n = {
                ...picker.i18n,
                formatDate: date => {
                    const { day, month, year } = date;
                    const d = new Date(year, month, day);
                    return new Intl.DateTimeFormat(undefined, {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                    }).format(d);
                },
                parseDate: dateStr => {
                    const date = new Date(dateStr);
                    return {
                        day: date.getDate(),
                        month: date.getMonth(),
                        year: date.getFullYear(),
                    };
                },
            };
        }
    }

    protected render() {
        return html`
            <vaadin-date-picker
                ${ref(this.datePickerRef)}
                @value-changed=${(e: DatePickerValueChangedEvent) => this.onValueChanged(e)}
                @change=${(e: DatePickerChangeEvent) => {
                    this.dispatchEvent(new CustomEvent(e.type, e));
                }}
                class="w-full"
                part="component"
                .autoOpenDisabled=${this.disableAutoOpen}
                .label=${this.label}
                .value=${dateToString(this.initial)}
                .max=${dateToString(this.max)}
                .min=${dateToString(this.min)}
                placeholder=${ifDefined(this.placeholder)}
                helper-text=${ifDefined(this.helperText)}
                .isDateDisabled=${this.isDateDisabled}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled}
            >
            <input slot="input" class="h-10"></input>
            </vaadin-date-picker>
        `;
    }

    protected validate() {
        const datepicker = this.datePickerRef.value;
        if (!datepicker) {
            return false;
        }
        return datepicker.validate();
    }

    private onValueChanged(e: DatePickerValueChangedEvent) {
        const { value } = e.detail;
        let date = value === '' ? undefined : new Date(value);

        if (date) {
            date = new Date(date.getTime() - date.getTimezoneOffset() * -60000);
        }

        // don't reset an equivalent date or an infinite loop can occur
        if (date?.getTime() === this.value?.getTime()) {
            return;
        }

        this.changeValue(date);
    }
}

/**
 * Convert a `Date` instance into an ISO 8601
 * [format](https://cdn.vaadin.com/vaadin-web-components/24.3.10/#/elements/vaadin-date-picker#property-value)
 * the `<vaadin-date-picker>` can bind to.
 */
function dateToString(d?: Date) {
    if (!d) {
        return '';
    }

    return d.toISOString().split('T')[0];
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmDatePicker;
    }
}
